import React, { Fragment } from 'react';
import { motion } from 'framer-motion';
export interface SplitPart {
  value: number;
  label?: string;
}
export interface SplitDiagramProps {
  /** The original number being split */
  original: number;
  /** The parts the number is split into */
  parts: SplitPart[];
  /** The operation context */
  operation?: 'add' | 'subtract' | 'multiply';
  /** The other operand (e.g., 48 in "48 + 37") */
  operand?: number;
  /** Final result */
  result?: number;
  /** Optional label */
  label?: string;
  /** 'stimulus' = compact, 'support' = educational panel */
  mode?: 'stimulus' | 'support';
}
const PART_COLORS = [{
  bg: 'bg-blue-100',
  border: 'border-blue-300',
  text: 'text-blue-800',
  dot: 'bg-blue-500'
}, {
  bg: 'bg-emerald-100',
  border: 'border-emerald-300',
  text: 'text-emerald-800',
  dot: 'bg-emerald-500'
}, {
  bg: 'bg-amber-100',
  border: 'border-amber-300',
  text: 'text-amber-800',
  dot: 'bg-amber-500'
}, {
  bg: 'bg-purple-100',
  border: 'border-purple-300',
  text: 'text-purple-800',
  dot: 'bg-purple-500'
}];
const OP_SYMBOL: Record<string, string> = {
  add: '+',
  subtract: '−',
  multiply: '×'
};
function formatNL(n: number): string {
  return new Intl.NumberFormat('nl-NL', {
    maximumFractionDigits: 4,
    minimumFractionDigits: 0
  }).format(n);
}
function SplitBranch({
  part,
  colorIdx,
  delay,
  isLast
}: {
  part: SplitPart;
  colorIdx: number;
  delay: number;
  isLast: boolean;
}) {
  const c = PART_COLORS[colorIdx % PART_COLORS.length];
  return <motion.div initial={{
    opacity: 0,
    y: 8
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    delay,
    duration: 0.3,
    ease: 'easeOut'
  }} className="flex items-center gap-2" data-id="element-14311" data-mp="zx7rrozimmgm">
      
      {/* Branch line */}
      <div className="flex flex-col items-center w-6" data-id="element-14312" data-mp="18v0az48cye9a">
        <div className={`w-3 h-3 rounded-full ${c.dot}`} data-id="element-14313" data-mp="2fc5afqrddtfm" />
        {!isLast && <div className="w-px h-4 bg-slate-300" data-id="element-14314" data-mp="2kc023wiyyn1q" />}
      </div>
      {/* Value chip */}
      <div className={`${c.bg} ${c.border} border rounded-lg px-3 py-1.5 ${c.text} font-bold text-sm`} data-id="element-14315" data-mp="ka0dtka1q5io">
        
        {formatNL(part.value)}
      </div>
    </motion.div>;
}
function StepArrow({
  from,
  op,
  partValue,
  to,
  delay,
  colorIdx
}: {
  from: number;
  op: string;
  partValue: number;
  to: number;
  delay: number;
  colorIdx: number;
}) {
  const c = PART_COLORS[colorIdx % PART_COLORS.length];
  return <motion.div initial={{
    opacity: 0,
    x: -8
  }} animate={{
    opacity: 1,
    x: 0
  }} transition={{
    delay,
    duration: 0.3,
    ease: 'easeOut'
  }} className="flex items-center gap-2 text-sm" data-id="element-14316" data-mp="1izg0zknwg0p">
      
      <span className="font-mono text-slate-600 w-16 text-right" data-id="element-14317" data-mp="ba3xq71q2ez8">
        {formatNL(from)}
      </span>
      <span className={`font-bold ${c.text}`} data-id="element-14318" data-mp="6hu0s726weng">
        {op} {formatNL(partValue)}
      </span>
      <svg className="w-4 h-4 text-slate-400 flex-shrink-0" viewBox="0 0 16 16" fill="none" data-id="element-14319" data-mp="3ex4aa08esm59">
        
        <path d="M3 8h10M10 5l3 3-3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-14320" data-mp="39yj9nzp8w0ga" />
        
      </svg>
      <span className="font-bold text-slate-800" data-id="element-14321" data-mp="1u805rlhggtpf">{formatNL(to)}</span>
    </motion.div>;
}
export function SplitDiagram({
  original,
  parts,
  operation = 'add',
  operand,
  result,
  label,
  mode = 'support'
}: SplitDiagramProps) {
  if (!parts || parts.length === 0) {
    return <div className="bg-red-50 p-4 rounded-xl border border-red-200 text-center" data-id="element-14322" data-mp="1kg482bja75lq">
        <p className="text-red-600 text-sm font-medium" data-id="element-14323" data-mp="288b1p2xmgyhg">Ongeldige splitsing</p>
      </div>;
  }
  const op = OP_SYMBOL[operation] ?? '+';
  // Calculate intermediate results for the step-by-step view
  const steps: {
    from: number;
    partValue: number;
    to: number;
  }[] = [];
  if (operand !== undefined) {
    let current = operand;
    for (const part of parts) {
      const next = operation === 'subtract' ? current - part.value : operation === 'multiply' ? current * part.value : current + part.value;
      steps.push({
        from: current,
        partValue: part.value,
        to: next
      });
      current = next;
    }
  }
  // Stimulus mode: compact
  if (mode === 'stimulus') {
    return <div className="bg-indigo-50 rounded-xl px-5 py-4 border border-indigo-200" aria-label={label ?? `Splitsstrategie: ${formatNL(original)}`} data-id="element-14324" data-mp="138mnswk98n52">
        
        {label && <p className="text-sm font-semibold text-indigo-800 mb-3 text-center" data-id="element-14325" data-mp="uaaqycssot46">
            {label}
          </p>}
        <div className="flex items-center justify-center gap-3 flex-wrap" data-id="element-14326" data-mp="1qz09848c3nqo">
          {/* Original number */}
          <motion.div initial={{
          scale: 0.8,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} transition={{
          duration: 0.3
        }} className="bg-indigo-200 border border-indigo-400 rounded-lg px-4 py-2 font-bold text-indigo-900 text-lg" data-id="element-14327" data-mp="1m18i3cfik5nj">
            
            {formatNL(original)}
          </motion.div>
          {/* Arrow */}
          <svg className="w-5 h-5 text-indigo-400 flex-shrink-0" viewBox="0 0 16 16" fill="none" data-id="element-14328" data-mp="1jakzwawlf9v">
            
            <path d="M3 8h10M10 5l3 3-3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-14329" data-mp="2d5un9vtd5tpr" />
            
          </svg>
          {/* Parts */}
          <div className="flex items-center gap-1.5" data-id="element-14330" data-mp="2m72g3yd422cg">
            {parts.map((part, i) => {
            const c = PART_COLORS[i % PART_COLORS.length];
            return <Fragment key={i} data-id="element-14331" data-mp="2vyyl7tdm6sq6" data-mpi={i}>
                  {i > 0 && <span className="text-slate-400 font-bold text-sm" data-id="element-14332" data-mp="2vy6yz95wq7ck">+</span>}
                  <motion.div initial={{
                scale: 0.8,
                opacity: 0
              }} animate={{
                scale: 1,
                opacity: 1
              }} transition={{
                delay: 0.15 + i * 0.1,
                duration: 0.3
              }} className={`${c.bg} ${c.border} border rounded-lg px-3 py-1.5 font-bold ${c.text} text-sm`} data-id="element-14333" data-mp="30w8ejk44joja">
                    
                    {formatNL(part.value)}
                  </motion.div>
                </Fragment>;
          })}
          </div>
        </div>
      </div>;
  }
  // Support mode: educational panel with step-by-step
  return <div className="bg-gradient-to-br from-indigo-50 to-purple-50 p-6 rounded-2xl border-2 border-indigo-200 shadow-sm" aria-label={label ?? `Splitsstrategie: ${formatNL(original)}`} data-id="element-14334" data-mp="2dxue9yuzd9o9">
      
      {label && <div className="mb-5 text-center" data-id="element-14335" data-mp="2nsw6j3kjyrv2">
          <div className="inline-flex items-center gap-2 bg-white/90 rounded-xl px-4 py-2 border border-indigo-200 shadow-sm" data-id="element-14336" data-mp="31tf7xruiizo9">
            <div className="w-2 h-2 rounded-full bg-indigo-500 animate-pulse" data-id="element-14337" data-mp="2wu2mnbjai0ns" />
            <span className="text-sm font-bold text-indigo-900" data-id="element-14338" data-mp="3bk1kfd6xna2q">{label}</span>
          </div>
        </div>}

      {/* Split visualization */}
      <div className="bg-white/80 rounded-xl p-4 border border-indigo-100 mb-4" data-id="element-14339" data-mp="36lgrzzxx9uou">
        <div className="flex items-start gap-4" data-id="element-14340" data-mp="3kl7zrixuieue">
          {/* Original number */}
          <motion.div initial={{
          scale: 0.8,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} transition={{
          duration: 0.3
        }} className="bg-indigo-200 border-2 border-indigo-400 rounded-xl px-5 py-3 font-bold text-indigo-900 text-xl flex-shrink-0" data-id="element-14341" data-mp="3fmeozy9y11xx">
            
            {formatNL(original)}
          </motion.div>

          {/* Branch connector */}
          <div className="flex flex-col items-center pt-2" data-id="element-14342" data-mp="3u691c0tsrg77">
            <svg className="w-6 h-6 text-slate-400" viewBox="0 0 24 24" fill="none" data-id="element-14343" data-mp="3ggdxfh9t7k0c">
              
              <path d="M5 12h14M14 7l5 5-5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-14344" data-mp="3l7nl4cbssd9l" />
              
            </svg>
          </div>

          {/* Parts */}
          <div className="space-y-1" data-id="element-14345" data-mp="3bgswj1b2x1kz">
            {parts.map((part, i) => <SplitBranch key={i} part={part} colorIdx={i} delay={0.2 + i * 0.12} isLast={i === parts.length - 1} data-id="element-14346" data-mp="2xhta80b1gyrz" data-mpi={i} />)}
          </div>
        </div>
      </div>

      {/* Step-by-step calculation (only when operand is provided) */}
      {steps.length > 0 && <div className="bg-white/80 rounded-xl p-4 border border-indigo-100 mb-4" data-id="element-14347" data-mp="3hxl0sw0nhkz5">
          <h4 className="text-xs font-bold text-indigo-700 uppercase tracking-wide mb-3" data-id="element-14348" data-mp="3czje8140n3ix">
            Stap voor stap
          </h4>
          <div className="space-y-2" data-id="element-14349" data-mp="37ywu2te48sar">
            {steps.map((step, i) => <StepArrow key={i} from={step.from} op={op} partValue={step.partValue} to={step.to} delay={0.4 + i * 0.15} colorIdx={i} data-id="element-14350" data-mp="33zwcfe7djvpc" data-mpi={i} />)}
          </div>
          {result !== undefined && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.4 + steps.length * 0.15
      }} className="mt-3 pt-3 border-t border-indigo-100 text-center" data-id="element-14351" data-mp="2u9lb7zf2s0m5">
          
              <span className="text-sm text-slate-600" data-id="element-14352" data-mp="2kcgkdmaabgz0">Uitkomst: </span>
              <span className="font-bold text-lg text-indigo-900" data-id="element-14353" data-mp="2ga21x3aj2j89">
                {formatNL(result)}
              </span>
            </motion.div>}
        </div>}

      {/* Educational explanation */}
      <div className="bg-white/80 rounded-xl p-4 border border-indigo-100 shadow-sm" data-id="element-14354" data-mp="qsy6tmpe9a8i">
        <div className="flex items-start gap-3" data-id="element-14355" data-mp="vr9ebqs7v8nk">
          <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0" data-id="element-14356" data-mp="vpyajqyru0wg">
            <span className="text-indigo-600 text-sm" data-id="element-14357" data-mp="ztw9o1o3jbpi">✂️</span>
          </div>
          <div data-id="element-14358" data-mp="14qwjmno2gylw">
            <h4 className="text-sm font-bold text-indigo-900 mb-1" data-id="element-14359" data-mp="14remi358ib2s">
              Splitsstrategie
            </h4>
            <p className="text-sm text-slate-700 leading-relaxed" data-id="element-14360" data-mp="7zublwwj2pbs">
              Bij splitsen verdeel je <strong data-id="element-14361" data-mp="c1fsrug3hprs">{formatNL(original)}</strong> in
              handige stukken:{' '}
              {parts.map((p, i) => <Fragment key={i} data-id="element-14362" data-mp="h0sf8e320utu" data-mpi={i}>
                  {i > 0 && (i === parts.length - 1 ? ' en ' : ', ')}
                  <strong data-id="element-14363" data-mp="3kjbxqovxkmbg">{formatNL(p.value)}</strong>
                </Fragment>)}
              . Zo kun je stap voor stap rekenen.
            </p>
          </div>
        </div>
      </div>
    </div>;
}